import { FC, useCallback } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton } from '@mui/material';
import { useDropzone } from 'react-dropzone';

import CloudIcon from '../../assets/icons/cloud-upload-outlined.png';
import DeleteIcon from '../../assets/icons/documents/Trash, Delete, Bin.png';
import PdfIcon from '../../assets/icons/documents/pdf_OneDrive_icon 1.png';

import styles from './FileUploader.module.scss';

interface FileUploaderProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  acceptedTypes?: Record<string, string[]>;
  maxFileSize?: number;
  placeholder?: {
    title?: string;
    subtitle?: string;
  };
  className?: string;
  disabled?: boolean;
}

const FileUploader: FC<FileUploaderProps> = ({
  files,
  onFilesChange,
  onRemoveFile,
  acceptedTypes = {
    'image/*': ['.png', '.jpg', '.jpeg'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  },
  maxFileSize = 10 * 1024 * 1024,
  placeholder = {
    title: 'Select your file or drag and drop',
    subtitle: 'png, pdf, jpg, docx accepted',
  },
  className = '',
  disabled = false,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;

      const validFiles = acceptedFiles.filter(file => {
        if (file.size > maxFileSize) {
          console.warn(`File ${file.name} is too large. Max size: ${maxFileSize / 1024 / 1024}MB`);
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        const newFiles = [...files, ...validFiles];
        onFilesChange(newFiles);
      }
    },
    [files, onFilesChange, maxFileSize, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    disabled,
  });

  return (
    <Box className={`${styles.fileUploader} ${className}`}>
      <Box
        {...getRootProps()}
        className={`${styles.dropzone} ${isDragActive ? styles.dragActive : ''} ${disabled ? styles.disabled : ''}`}
      >
        <input {...getInputProps()} />
        <img
          src={CloudIcon}
          alt="cloud"
          className={styles.cloudIcon}
        />
        <Typography
          variant="h12Semibold"
          gutterBottom
          sx={{ color: '#222B3A' }}
        >
          {placeholder.title}
        </Typography>
        <Typography
          variant="h13"
          sx={{ color: '#5E6A81' }}
        >
          {placeholder.subtitle}
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box className={styles.filesContainer}>
          <List className={styles.filesList}>
            {files.map((file, index) => (
              <ListItem
                key={`${file.name}-${index}`}
                className={styles.fileItem}
                secondaryAction={
                  !disabled && (
                    <IconButton
                      edge="end"
                      onClick={() => onRemoveFile(index)}
                      className={styles.deleteButton}
                    >
                      <img
                        src={DeleteIcon}
                        alt="delete"
                        className={styles.deleteIcon}
                      />
                    </IconButton>
                  )
                }
              >
                <ListItemIcon className={styles.fileIcon}>
                  <img
                    src={PdfIcon}
                    alt="file icon"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  primaryTypographyProps={{ variant: 'h11' }}
                  secondaryTypographyProps={{ variant: 'h12' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FileUploader;
